import { toUnityFixed } from '../../../../helpers/unityHelper';
import { changeIntensity, disableGameObject, enableGameObject, removeDisplacement, selectObjectBeforeUndoRedo, updateDisplacementMapChangedWithCycle } from '../ProductBuilderComponents/commonHelper';
import { TILING_TYPE } from '../../../../config/constant/unityConstants';
import { convertActiveUnitToCM } from '../../../../helpers/curatorActions';
import { undoRedo, undoRedo as undoRedoObject } from './UndoRedo';
import { selectSelectedHeight } from '../../../../redux/slicers/productBuilder/productBuilder.slicer';
import { getAppState } from '../../../../redux/store';
import { selectSelectedObjectName } from '../../../../redux/slicers/admin/curatorUnityObjectSlicer';
import { UnityEvent, waitForEventOnce } from '../../../../helpers/unityPubSub';

export class UndoRedoTopBottom {
  constructor({ undoRedoInstance }) {
    this.undoRedo = undoRedoInstance;
    this.pendingChanges = new Map();
    this.createAction = this.undoRedo.createAction;
    this._actionCounter = 0; // debug sequencing
    this._groupActive = false;
    this._groupBuffer = [];
    this._initActions();
  }

  beginGroup() {
    this._groupActive = true;
    this._groupBuffer = [];
  }

  commitGroup() {
    if (!this._groupActive || this._groupBuffer.length === 0) {
      this._groupActive = false;
      this._groupBuffer = [];
      return;
    }
    const actionItems = this._groupBuffer.map(c => ({
      prevValue: this._clone(c.prevValue),
      nextValue: this._clone(c.nextValue),
      unityPropertyName: c.unityPropertyName,
      objectName: c.objectName,
      componentType: c.componentType,
      updateCallback: c.updateCallback,
      index: c.index
    }));
    this._groupActive = false;
    this._groupBuffer = [];
    // Push one composite action
    this.undoRedo.createAction(() => ({
      __ACTION__: 'TOP_BOTTOM_PROP_CHANGE_GROUP',
      undo: async () => {
        for (const it of actionItems.slice().reverse()) {
          it.updateCallback?.(it.index, it.unityPropertyName, this._clone(it.prevValue));
          await this._applyUnityChange(it.unityPropertyName, it.prevValue);
        }
        updateDisplacementMapChangedWithCycle();
      },
      redo: async () => {
        for (const it of actionItems) {
          it.updateCallback?.(it.index, it.unityPropertyName, this._clone(it.nextValue));
          await this._applyUnityChange(it.unityPropertyName, it.nextValue);
        }
        updateDisplacementMapChangedWithCycle();
      }
    }), { pause: true, name: 'TOP_BOTTOM_PROP_CHANGE_GROUP' })(); // paused
  }

  cancelGroup() {
    this._groupActive = false;
    this._groupBuffer = [];
  }

  _initActions() {
    this._pushPropertyChange = this.createAction(
      ({ prevValue, nextValue, unityPropertyName, objectName, componentType, updateCallback, index }) => {
        // Compare values
        const isSame = typeof prevValue === 'object' 
          ? prevValue.width === nextValue.width && prevValue.height === nextValue.height
          : prevValue === nextValue;
        
        if (isSame) return false;
        if (this._groupActive) {
          this._groupBuffer.push({ prevValue, nextValue, unityPropertyName, objectName, componentType, updateCallback, index });
          return false; // do not create individual action while grouping
        }
        const actionId = ++this._actionCounter;
        const selectedObjectName = selectSelectedObjectName(getAppState());    
        return {
          __ACTION__: 'TOP_BOTTOM_PROP_CHANGE',
          _id: actionId,
          undo: async () => {
            await selectObjectBeforeUndoRedo(selectedObjectName)        
            updateCallback?.(index, unityPropertyName, this._clone(prevValue));
            await this._applyUnityChange(unityPropertyName, prevValue);
            updateDisplacementMapChangedWithCycle();
          },
          redo: async () => {
            await selectObjectBeforeUndoRedo(selectedObjectName) 
            updateCallback?.(index, unityPropertyName, this._clone(nextValue));
            await this._applyUnityChange(unityPropertyName, nextValue);
            updateDisplacementMapChangedWithCycle();
          },
        };
      },
      { pause: true, name: 'TOP_BOTTOM_PROP_CHANGE' } // ensure paused during undo/redo
    );
    // Add/Remove Top Bottom action
    this._pushAddRemove = this.createAction(({ objectName, componentType, refreshCallback, oldHeight, newHeight, updateHeightCallback }) => {
      if (!objectName) return false;
      return {
        __ACTION__: 'TOP_BOTTOM_ADD_REMOVE',
        undo: async () => {
          // Update height if it changed during add operation
          if (oldHeight && newHeight && oldHeight !== newHeight && updateHeightCallback) {
            updateHeightCallback(oldHeight);
          }
          
          const selectedHeight = selectSelectedHeight(getAppState());
          disableGameObject({
            propInfoCommandDatas: [{ objectName }],
            productBuilderModel: { height: selectedHeight?.value || "12" }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
        redo: async () => {
          // Update height if it changed during add operation
          if (oldHeight && newHeight && oldHeight !== newHeight && updateHeightCallback) {
            updateHeightCallback(newHeight);
          }
          
          const selectedHeight = selectSelectedHeight(getAppState());
          enableGameObject({
            propInfoCommandDatas: [{ objectName }],
            productBuilderModel: { 
              height: selectedHeight?.value || "12",
              operationType: componentType === "top" ? 'AddTopAndTape' : "AddBottomAndTape",
            }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
      };
    });

    // Delete top/bottom action (opposite of add)
    this._pushDelete = this.createAction(({ objectName, componentType, refreshCallback, oldHeight, newHeight, updateHeightCallback }) => {
      if (!objectName) return false;
      return {
        __ACTION__: 'TOP_BOTTOM_DELETE',
        undo: async () => {
          // Update height if it changed during delete operation
          if (oldHeight && newHeight && oldHeight !== newHeight && updateHeightCallback) {
            updateHeightCallback(oldHeight);
          }
          
          const selectedHeight = selectSelectedHeight(getAppState());
          // Undo delete = restore the top/bottom (enable)
          enableGameObject({
            propInfoCommandDatas: [{ objectName }],
            productBuilderModel: { 
              height: selectedHeight?.value || "12",
              operationType: componentType === "top" ? 'AddTopAndTape' : "AddBottomAndTape",
            }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
        redo: async () => {
          // Update height if it changed during delete operation
          if (oldHeight && newHeight && oldHeight !== newHeight && updateHeightCallback) {
            updateHeightCallback(newHeight);
          }
          
          const selectedHeight = selectSelectedHeight(getAppState());
          // Redo delete = delete again (disable)
          disableGameObject({
            propInfoCommandDatas: [{ objectName }],
            productBuilderModel: { height: selectedHeight?.value || "12" }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
      };
    });

    // Replace Top/Bottom action (change type)
    this._pushReplace = this.createAction(
      ({ oldData, newData, oldObjectName, newObjectName, componentType, refreshCallback, oldHeight, newHeight, updateHeightCallback }) => {
        if (!oldData || !newData || !oldObjectName) return false;
        return {
          __ACTION__: 'TOP_BOTTOM_REPLACE',
        undo: async () => {
          console.log('[UndoRedoTopBottom][UNDO REPLACE] Disabling new:', newObjectName, 'Enabling old:', oldObjectName);
          
          // Update height if it changed during replace operation
          if (oldHeight && newHeight && oldHeight !== newHeight && updateHeightCallback) {
            updateHeightCallback(oldHeight);
          }
          
          const selectedHeight = selectSelectedHeight(getAppState());
          
          // First: Disable the new top/bottom
          disableGameObject({
            propInfoCommandDatas: [{ objectName: newObjectName }],
            productBuilderModel: {
              height: selectedHeight?.value || "12",
            }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Second: Enable the old top/bottom
          enableGameObject({
            propInfoCommandDatas: [{ objectName: oldObjectName }],
            productBuilderModel: {
              height: selectedHeight?.value || "12",
              objectNameToDisable: newObjectName,
              operationType: 'Modify'
            }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Refresh after both operations
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
        redo: async () => {
          console.log('[UndoRedoTopBottom][REDO REPLACE] Disabling old:', oldObjectName, 'Enabling new:', newObjectName);
          
          // Update height if it changed during replace operation
          if (oldHeight && newHeight && oldHeight !== newHeight && updateHeightCallback) {
            updateHeightCallback(newHeight);
          }
          
          const selectedHeight = selectSelectedHeight(getAppState());
          
          // First: Disable the old top/bottom
          disableGameObject({
            propInfoCommandDatas: [{ objectName: oldObjectName }],
            productBuilderModel: {
              height: selectedHeight?.value || "12",
            }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Second: Enable the new top/bottom
          enableGameObject({
            propInfoCommandDatas: [{ objectName: newObjectName }],
            productBuilderModel: {
              height: selectedHeight?.value || "12",
              objectNameToDisable: oldObjectName,
              operationType: 'Modify'
            }
          });

          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Refresh after both operations
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
      };
    });

    // Pattern/Gusset apply action
    this._pushPatternApply = this.createAction(
      ({ oldPatternData, newPatternData, objectName, componentType, isGusset, applyTextureCallback, refreshCallback, selectObjectCallback }) => {
        if (!newPatternData) return false;
        return {
          __ACTION__: 'TOP_BOTTOM_PATTERN_APPLY',
          undo: async () => {

            // IMPORTANT: First select the correct object (main or gusset)
            if (selectObjectCallback) {
              await selectObjectCallback(isGusset);
            }

            // Small delay to ensure object selection completes
            await new Promise((resolve) => setTimeout(resolve, 100));

            if (oldPatternData) {
              // Apply old pattern
              await applyTextureCallback?.(oldPatternData);
            } else {
              // Remove pattern if no old pattern existed
              removeDisplacement();
            }

            // Small delay to ensure Unity processes
            await new Promise((resolve) => setTimeout(resolve, 50));

            // Refresh after operation
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
          redo: async () => {
            // IMPORTANT: First select the correct object (main or gusset)
            if (selectObjectCallback) {
              await selectObjectCallback(isGusset);
            }

            // Small delay to ensure object selection completes
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Apply new pattern
            await applyTextureCallback?.(newPatternData);

            // Small delay to ensure Unity processes
            await new Promise((resolve) => setTimeout(resolve, 50));

            // Refresh after operation
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
        };
      }
    );

    // Mirror toggle action
    this._pushMirrorToggle = this.createAction(
      ({
        oldObjectNames,
        newObjectNames,
        wasMirrored,
        becameMirrored,
        oldHeight,
        newHeight,
        updateHeightCallback,
        refreshCallback,
        updateToggle,
        oldToggleValue,
        newToggleValue,
      }) => {
        if (wasMirrored === becameMirrored) return false;
        // Ensure we have arrays
        const oldNames = Array.isArray(oldObjectNames) ? oldObjectNames : [oldObjectNames].filter(Boolean);
        const newNames = Array.isArray(newObjectNames) ? newObjectNames : [newObjectNames].filter(Boolean);
        
        return {
          __ACTION__: 'TOP_BOTTOM_MIRROR_TOGGLE',
          undo: async () => {
            // Set flag to skip height confirmation during undo/redo
            this._isUndoRedoOperation = true;
            updateToggle(oldToggleValue);
            // Update height first if it changed
            if (oldHeight && newHeight && oldHeight !== newHeight) {
              updateHeightCallback?.(oldHeight);
            }

            const currentHeight = selectSelectedHeight(getAppState())?.value || '12';

            // First: Disable all new bottom components
            if (newNames.length > 0) {
              disableGameObject({
                propInfoCommandDatas: newNames.map(objectName => ({ objectName })),
                productBuilderModel: {
                  height: currentHeight,
                },
              });
              await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
            }

            // Second: Enable all old bottom components
            if (oldNames.length > 0) {
              enableGameObject({
                propInfoCommandDatas: oldNames.map(objectName => ({ objectName })),
                productBuilderModel: {
                  height: currentHeight,
                  operationType: 'Modify',
                },
              });
              await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
            }

            // Refresh after both operations
            refreshCallback?.();
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);

            // Clear flag after operation
            this._isUndoRedoOperation = false;
          },
          redo: async () => {
            // Set flag to skip height confirmation during undo/redo
            this._isUndoRedoOperation = true;
            updateToggle(newToggleValue);
            // Update height first if it changed
            if (oldHeight && newHeight && oldHeight !== newHeight) {
              updateHeightCallback?.(newHeight);
            }

            const currentHeight = selectSelectedHeight(getAppState())?.value || '12';

            // First: Disable all old bottom components
            if (oldNames.length > 0) {
              disableGameObject({
                propInfoCommandDatas: oldNames.map(objectName => ({ objectName })),
                productBuilderModel: {
                  height: currentHeight,
                },
              });
              await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
            }

            // Second: Enable all new bottom components
            if (newNames.length > 0) {
              enableGameObject({
                propInfoCommandDatas: newNames.map(objectName => ({ objectName })),
                productBuilderModel: {
                  height: currentHeight,
                  operationType: 'Modify',
                },
              });
              await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
            }

            // Refresh after both operations
            refreshCallback?.();
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);

            // Clear flag after operation
            this._isUndoRedoOperation = false;
          },
        };
      },
      { pause: true, name: 'TOP_BOTTOM_MIRROR_TOGGLE' }
    );

    // Quilting Pattern apply action (separate from main pattern apply)
    this._pushQuiltingPatternApply = this.createAction(
      ({ oldPatternData, newPatternData, objectName, componentType, isGusset, applyTextureCallback, refreshCallback, selectObjectCallback }) => {
        if (!newPatternData) return false;
        const selectedObjectName = selectSelectedObjectName(getAppState()); 
        return {
          __ACTION__: 'TOP_BOTTOM_QUILTING_PATTERN_APPLY',
          undo: async () => {
            // IMPORTANT: First select the correct object (main or gusset)
            await selectObjectBeforeUndoRedo(selectedObjectName)        
            if (selectObjectCallback) {
              await selectObjectCallback(isGusset);
            }

            // Small delay to ensure object selection completes
            await new Promise((resolve) => setTimeout(resolve, 10));

            if (oldPatternData) {
              // Apply old quilting pattern
              await applyTextureCallback?.(oldPatternData);
            } else {
              // Remove quilting pattern if no old pattern existed
              removeDisplacement();
            }

            // Refresh after operation
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
          redo: async () => {
            await selectObjectBeforeUndoRedo(selectedObjectName)        
            // IMPORTANT: First select the correct object (main or gusset)
            if (selectObjectCallback) {
              await selectObjectCallback(isGusset);
            }

            // Small delay to ensure object selection completes
            await new Promise((resolve) => setTimeout(resolve, 10));

            // Apply new quilting pattern
            await applyTextureCallback?.(newPatternData);


            // Refresh after operation
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
        };
      }
    );

    // Quilting Pattern remove action
    this._pushQuiltingPatternRemove = this.createAction(
      ({ patternData, objectName, componentType, isGusset, applyTextureCallback, removeTextureCallback, refreshCallback, selectObjectCallback, prevValue }) => {
        if (!patternData) return false;
        return {
          __ACTION__: 'TOP_BOTTOM_QUILTING_PATTERN_REMOVE',
          undo: async () => {
            // IMPORTANT: First select the correct object (main or gusset)
            if (selectObjectCallback) {
              await selectObjectCallback(isGusset);
            }

            // Small delay to ensure object selection completes
            await new Promise((resolve) => setTimeout(resolve, 10));

            // Restore the quilting pattern
            await applyTextureCallback?.(patternData, prevValue);

            // Small delay to ensure Unity processes
            await new Promise((resolve) => setTimeout(resolve, 30));

            // Refresh after operation
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
          redo: async () => {
            // IMPORTANT: First select the correct object (main or gusset)
            if (selectObjectCallback) {
              await selectObjectCallback(isGusset);
            }

            // Small delay to ensure object selection completes
            await new Promise((resolve) => setTimeout(resolve, 10));

            // Remove the quilting pattern
            await removeTextureCallback?.();

            // Small delay to ensure Unity processes
            await new Promise((resolve) => setTimeout(resolve, 10));

            // Refresh after operation
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
        };
      }
    );

    // Card selection action (expand/collapse)
    this._pushCardSelection = this.createAction(
      ({ previousIndex, newIndex, componentType, selectObjectCallback }) => {
        if (previousIndex === newIndex || previousIndex === -1) return false;
        return {
          __ACTION__: 'TOP_BOTTOM_CARD_SELECTION',
          undo: async () => {
            await selectObjectCallback?.(previousIndex);
          },
          redo: async () => {
            await selectObjectCallback?.(newIndex);
          },
        };
      },
      { pause: true, name: 'TOP_BOTTOM_CARD_SELECTION' }
    );
  }

  _clone(v) {
    return typeof v === 'object' && v !== null ? { ...v } : v;
  }

  _applyUnityChange = async (unityPropertyName, value) => {
    // Match the exact Unity method calls from the normal flow
    
    // Width/Height (displacement)
    if (unityPropertyName === 'displacement' || unityPropertyName === 'displacementGusset') {
      if (this.undoRedo.unityRedux?.changeSize) {
        const payload = { width: value.width, height: value.height, tilingOptionType: TILING_TYPE.DISPLACEMENT, count: 3 };
        this.undoRedo.unityRedux.changeSize(payload);
      }
    } 
    // Intensity (depth)
    else if (unityPropertyName === 'displacementIntensity' || unityPropertyName === 'displacementIntensityGusset') {
      // Call changeIntensity exactly like handleChangeIntensity does
      changeIntensity(value);
    } 
    // Position X/Y
    else if (unityPropertyName === 'positionX' || unityPropertyName === 'positionY' || 
             unityPropertyName === 'positionXGusset' || unityPropertyName === 'positionYGusset') {
      if (this.undoRedo.unityRedux?.changePosition) {
        const isGusset = unityPropertyName.includes('Gusset');
        const axis = unityPropertyName.toLowerCase().includes('x') ? 'x' : 'y';
        const otherAxis = axis === 'x' ? 'y' : 'x';
        const otherAxisProp = isGusset 
          ? (otherAxis === 'x' ? 'positionXGusset' : 'positionYGusset')
          : (otherAxis === 'x' ? 'positionX' : 'positionY');
        
        const payload = {
          x: axis === 'x' ? value : this._getTempValue(otherAxisProp),
          y: axis === 'y' ? value : this._getTempValue(otherAxisProp),
          tilingOptionType: TILING_TYPE.DISPLACEMENT,
          count: 3,
        };
        this.undoRedo.unityRedux.changePosition(payload);
      }
    } 
    // Rotation
    else if (unityPropertyName === 'rotation' || unityPropertyName === 'rotationGusset') {
      if (this.undoRedo.unityRedux?.changeRotation) {
        const isGusset = unityPropertyName.includes('Gusset');
        
        const payload = {
          width: this._getTempValue(isGusset ? 'widthGusset' : 'width'),
          height: this._getTempValue(isGusset ? 'heightGusset' : 'height'),
          x: this._getTempValue(isGusset ? 'positionXGusset' : 'positionX'),
          y: this._getTempValue(isGusset ? 'positionYGusset' : 'positionY'),
          tilingOptionType: TILING_TYPE.DISPLACEMENT,
          rotation: value,
        };
        // this.undoRedo.unityRedux.changeRotation(payload);
      }
    }
  };

  _getTempValue = (propName) => {
    const mapping = {
      width: this._tempWidth,
      height: this._tempHeight,
      positionX: this._tempPositionX,
      positionY: this._tempPositionY,
      widthGusset: this._tempWidthGusset,
      heightGusset: this._tempHeightGusset,
      positionXGusset: this._tempPositionXGusset,
      positionYGusset: this._tempPositionYGusset,
    };
    return mapping[propName] || 0;
  };

  _wrapUpdateCallback = (originalCallback) => {
    return (index, unityPropertyName, value) => {
      // Store temp values for position/rotation
      if (unityPropertyName === 'displacement' && typeof value === 'object') {
        this._tempWidth = value.width;
        this._tempHeight = value.height;
      } else if (unityPropertyName === 'displacementGusset' && typeof value === 'object') {
        this._tempWidthGusset = value.width;
        this._tempHeightGusset = value.height;
      } else if (unityPropertyName === 'positionX') {
        this._tempPositionX = value;
      } else if (unityPropertyName === 'positionY') {
        this._tempPositionY = value;
      } else if (unityPropertyName === 'positionXGusset') {
        this._tempPositionXGusset = value;
      } else if (unityPropertyName === 'positionYGusset') {
        this._tempPositionYGusset = value;
      }
      
      originalCallback?.(index, unityPropertyName, value);
    };
  };

  // This is called by UndoRedoLockedInput for Width/Height
  changeSize = ({ name, prevValue, nextValue, data }) => {
    undoRedoObject.resume();
    const { objectName, componentType, updateCallback, index } = data;
    const isGusset = componentType === 'gusset';
    const unityPropertyName = isGusset ? 'displacementGusset' : 'displacement';
    
    // Values from UndoRedoLockedInput are in display units (inches/cm)
    // Convert to CM for Unity
    const convertValue = (val) => {
      if (typeof val === 'object') {
        return {
          width: toUnityFixed(convertActiveUnitToCM(val.width), 3),
          height: toUnityFixed(convertActiveUnitToCM(val.height), 3),
        };
      }
      return toUnityFixed(convertActiveUnitToCM(val), 3);
    };
    
    const prev = convertValue(prevValue);
    const next = convertValue(nextValue);
    
    this._pushPropertyChange({
      prevValue: prev,
      nextValue: next,
      unityPropertyName,
      objectName,
      componentType: isGusset ? 'Top' : componentType.charAt(0).toUpperCase() + componentType.slice(1),
      updateCallback: this._wrapUpdateCallback(updateCallback),
      index,
    });
    undoRedoObject.pause();
  };

  startPropertyChange({ objectName, unityPropertyName, initialValue, index }) {
    undoRedoObject.resume();
    const key = `${objectName}__${unityPropertyName}`;
    
    const normalizedValue = typeof initialValue === 'object' 
      ? { width: toUnityFixed(initialValue.width, 3), height: toUnityFixed(initialValue.height, 3) }
      : toUnityFixed(initialValue, 3);
    
    this.pendingChanges.set(key, {
      objectName,
      unityPropertyName,
      initialValue: normalizedValue,
      lastValue: normalizedValue,
      index,
      startedAt: Date.now(),
    });
    undoRedoObject.pause();
  }

  updatePendingChange({ objectName, unityPropertyName, currentValue }) {
    const key = `${objectName}__${unityPropertyName}`;
    const item = this.pendingChanges.get(key);
    if (!item) return;
    
    const normalizedValue = typeof currentValue === 'object'
      ? { width: toUnityFixed(currentValue.width, 3), height: toUnityFixed(currentValue.height, 3) }
      : toUnityFixed(currentValue, 3);
    
    item.lastValue = normalizedValue;
    this.pendingChanges.set(key, item);
  }

  endPropertyChange({
    objectName,
    unityPropertyName,
    finalValue,
    componentType,
    updateCallback,
    index,
  }) {
    undoRedoObject.resume();
    const key = `${objectName}__${unityPropertyName}`;
    const item = this.pendingChanges.get(key);
    if (!item) return;
    const prevValue = this._clone(item.initialValue);
    // Use tracked lastValue to ensure accurate final state for redo chain
    const nextValue = this._clone(item.lastValue);
    if (this._groupActive) {
      // buffer instead of immediate action
      this._groupBuffer.push({
        prevValue,
        nextValue,
        unityPropertyName,
        objectName,
        componentType: componentType.charAt(0).toUpperCase() + componentType.slice(1),
        updateCallback: this._wrapUpdateCallback(updateCallback),
        index,
      });
      this.pendingChanges.delete(key);
      return;
    }
    this._pushPropertyChange({
      prevValue,
      nextValue,
      unityPropertyName,
      objectName,
      componentType: componentType.charAt(0).toUpperCase() + componentType.slice(1),
      updateCallback: this._wrapUpdateCallback(updateCallback),
      index,
    });
    this.pendingChanges.delete(key);
    undoRedoObject.pause();
  }

  cancelPropertyChange({ objectName, unityPropertyName }) {
    const key = `${objectName}__${unityPropertyName}`;
    this.pendingChanges.delete(key);
  }

  cancelAllPendingChanges() {
    this.pendingChanges.clear();
  }

  clearAllPendingChanges() {
    this.pendingChanges.clear();
  }

  addTopBottomAction({ objectName, componentType, refreshCallback, oldHeight, newHeight, updateHeightCallback }) {
    undoRedo.resume();
    this._pushAddRemove({
      objectName,
      componentType,
      refreshCallback,
      oldHeight,
      newHeight,
      updateHeightCallback,
    });
    undoRedo.pause();
  }

  addTopBottomDeleteAction({ objectName, componentType, refreshCallback, oldHeight, newHeight, updateHeightCallback }) {
    undoRedo.resume();
    this._pushDelete({
      objectName,
      componentType,
      refreshCallback,
      oldHeight,
      newHeight,
      updateHeightCallback,
    });
    undoRedo.pause();
  }

  addTopBottomReplaceAction({ oldData, newData, oldObjectName, newObjectName, componentType, refreshCallback, oldHeight, newHeight, updateHeightCallback }) {
    undoRedo.resume();
    this._pushReplace({
      oldData,
      newData,
      oldObjectName,
      newObjectName,
      componentType,
      refreshCallback,
      oldHeight,
      newHeight,
      updateHeightCallback,
    });
    undoRedo.pause();
  }

  addPatternApplyAction({ oldPatternData, newPatternData, objectName, componentType, isGusset, applyTextureCallback, refreshCallback, selectObjectCallback }) {
    undoRedo.resume();
    this._pushPatternApply({
      oldPatternData,
      newPatternData,
      objectName,
      componentType,
      isGusset,
      applyTextureCallback,
      refreshCallback,
      selectObjectCallback,
    });
    undoRedo.pause();
  }

  addMirrorToggleAction({ 
    oldObjectNames, 
    newObjectNames, 
    wasMirrored, 
    becameMirrored, 
    oldHeight, 
    newHeight, 
    updateHeightCallback, 
    refreshCallback,
    updateToggle,
    oldToggleValue,
    newToggleValue,
   }) {
    undoRedo.resume();
    this._pushMirrorToggle({
      updateToggle,
      oldToggleValue,
      newToggleValue,
      oldObjectNames,
      newObjectNames,
      refreshCallback,
      wasMirrored,
      becameMirrored,
      oldHeight,
      newHeight,
      updateHeightCallback,
    });
    undoRedo.pause();
  }

  addQuiltingPatternApplyAction({ oldPatternData, newPatternData, objectName, componentType, isGusset, applyTextureCallback, refreshCallback, selectObjectCallback }) {
    undoRedo.resume();
    this._pushQuiltingPatternApply({
      oldPatternData,
      newPatternData,
      objectName,
      componentType,
      isGusset,
      applyTextureCallback,
      refreshCallback,
      selectObjectCallback,
    });
    undoRedo.pause();
  }

  addQuiltingPatternRemoveAction({ patternData, objectName, componentType, isGusset, applyTextureCallback, removeTextureCallback, refreshCallback, selectObjectCallback, prevValue }) {
    undoRedo.resume();
    this._pushQuiltingPatternRemove({
      patternData,
      objectName,
      componentType,
      isGusset,
      applyTextureCallback,
      removeTextureCallback,
      refreshCallback,
      selectObjectCallback,
      prevValue,
    });
    undoRedo.pause();
  }

  addCardSelectionAction({ previousIndex, newIndex, componentType, selectObjectCallback }) {
    undoRedo.resume();
    this._pushCardSelection({
      previousIndex,
      newIndex,
      componentType,
      selectObjectCallback,
    });
    undoRedo.pause();
  }
}
